import { useRef, useEffect, useState, useCallback, KeyboardEvent, ReactNode } from 'react';
import { Send, Square, Filter, Users, Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import useTranslation from '@/hooks/useTranslation';
import type { SuggestedQuestion, ResearchArtifact } from '@/reactQuery/researchAssistant';
import NonBlockingProgressStrip from '@/components/ui/NonBlockingProgressStrip';
import MessageBubble from './MessageBubble';
import SuggestedQuestions from './SuggestedQuestions';
import ToolProgressStepper from './ToolProgressStepper';
import type { ChatMessage, ToolStep } from './useResearchChat';
import ResearchAssistantErrorBanner from './ResearchAssistantErrorBanner';
import type { SafeErrorPayload } from './safeErrorTypes';

interface ChatThreadProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  activeToolSteps: ToolStep[];
  /** Structured error payload from useResearchChat. Replaces the previous `string | null`. */
  error: SafeErrorPayload | null;
  onSendMessage: (message: string) => void;
  onStopStreaming: () => void;
  /** Dismiss the current error banner */
  onDismissError?: () => void;
  /** Retry the last failed message */
  onRetry?: () => void;
  /** Opens the thread list — forwarded to ResearchAssistantErrorBanner for THREAD_* codes. */
  onOpenThreadList?: () => void;
  /** Resolves outdated analysis — forwarded for ANALYSIS_OUTDATED code. */
  onReRunAnalysis?: () => void;
  /**
   * D.8 triple-stack suppression: when D.8's sticky banner is visible
   * (schema not current OR run in flight), suppress the full red
   * ANALYSIS_OUTDATED banner and replace with a quieter inline notice.
   */
  suppressAnalysisOutdatedBanner?: boolean;
  /**
   * D.7: callback to regenerate a stale comparison inline.
   * Receives the comparison_id from the next_action payload.
   */
  onRegenerateComparison?: (comparisonId: string, filterIdA: string, filterIdB: string) => void;
  /**
   * D.7: whether a comparison regen is currently in flight (disables the button).
   * Also used for D.7-D.8 collision: disabled while D.8 full-pipeline run is active.
   */
  isRegeneratingComparison?: boolean;
  /** D.7: substep label from the regen progress strip */
  regenSubstep?: string;
  /** D.7: ISO timestamp of when the comparison regen started (for elapsed timer) */
  regenStartedAt?: string;
  projectTitle?: string;
  filterName?: string | null;
  interviewGoal?: string;
  respondentCount?: number;
  suggestions?: SuggestedQuestion[];
  /** True while the active thread is being fetched (after thread switch) */
  isLoadingThread?: boolean;
  /** True while creating a new thread (initial send before SSE starts) */
  isCreatingThread?: boolean;
  /** True when analysis is being re-run — disables input */
  isRerunLoading?: boolean;
  /** All artifacts in the current thread — passed to MessageBubble so
   *  citation pills can resolve conversation_id for loaded threads. */
  allArtifacts?: ResearchArtifact[];
  /** Project id — passed to MessageBubble so the citation-pill hook
   *  can fetch the project-level respondent_index → conversation_id
   *  lookup and make every `[R12]` pill clickable. */
  projectId?: string;
  /** Optional slot rendered on the right side of the chat header. Used
   *  at narrow widths to surface the artifact panel trigger button. */
  headerRight?: ReactNode;
}

/** Skeleton placeholders for the chat area while a thread is loading. */
function ChatSkeleton() {
  return (
    <div className="flex flex-col gap-4 px-2 pt-4 animate-pulse">
      {/* User message skeleton */}
      <div className="flex justify-end mb-2">
        <div className="flex items-start gap-2.5 max-w-[70%]">
          <div className="bg-neutral-100 rounded-2xl rounded-tr-sm h-8 w-48" />
          <div className="h-7 w-7 rounded-full bg-neutral-100 mt-0.5" />
        </div>
      </div>
      {/* Assistant message skeleton */}
      <div className="flex">
        <div className="flex items-start gap-2.5 max-w-[80%] w-full">
          <div className="h-7 w-7 rounded-full bg-neutral-100 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="h-3 bg-neutral-100 rounded w-full mb-2" />
            <div className="h-3 bg-neutral-100 rounded w-5/6 mb-2" />
            <div className="h-3 bg-neutral-100 rounded w-4/6" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatThread({
  messages,
  isStreaming,
  activeToolSteps,
  error,
  onSendMessage,
  onStopStreaming,
  onDismissError,
  onRetry,
  onOpenThreadList,
  onReRunAnalysis,
  suppressAnalysisOutdatedBanner,
  onRegenerateComparison,
  isRegeneratingComparison,
  regenSubstep,
  regenStartedAt,
  projectTitle,
  filterName,
  interviewGoal,
  respondentCount,
  suggestions = [],
  isLoadingThread,
  isCreatingThread,
  isRerunLoading,
  allArtifacts,
  projectId,
  headerRight,
}: ChatThreadProps) {
  const { lang } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom on new messages, but ONLY if the user is already
  // near the bottom. If they have scrolled up to re-read older messages,
  // we don't snap them back — that's a hostile UX.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNearBottom = distanceFromBottom < 150;
    if (isNearBottom) {
      messagesEndRef.current?.scrollIntoView({
        behavior: isStreaming ? 'auto' : 'smooth',
        block: 'end',
      });
    }
  }, [messages, activeToolSteps, isStreaming]);

  // Auto-resize textarea. Before the first message arrives we want a
  // hero-sized input (prominent, inviting, paired with a bigger send
  // button). Once the conversation has started we drop back to the
  // compact sizing so the input doesn't dominate the scrolling thread.
  // A minimum height is always applied so the textarea never visually
  // shrinks below its paired send button.
  const isEmptyState = messages.length === 0;
  const inputMinHeight = isEmptyState ? 60 : 48;
  const inputMaxHeight = isEmptyState ? 200 : 180;
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const next = Math.min(
        Math.max(textareaRef.current.scrollHeight, inputMinHeight),
        inputMaxHeight,
      );
      textareaRef.current.style.height = `${next}px`;
    }
  }, [inputValue, inputMinHeight, inputMaxHeight]);

  const handleSubmit = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || isStreaming || isCreatingThread) return;
    onSendMessage(trimmed);
    setInputValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [inputValue, isStreaming, isCreatingThread, onSendMessage]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Cmd/Ctrl+Enter and plain Enter both submit; Shift+Enter inserts newline.
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleSuggestionSelect = useCallback(
    (question: string) => {
      onSendMessage(question);
    },
    [onSendMessage],
  );

  const hasMessages = messages.length > 0;
  const showLoading = isLoadingThread && !hasMessages;
  // Show "Connecting..." indicator after the user sends but before the SSE
  // starts emitting deltas. We detect this by streaming + last message is
  // an empty assistant message + no tool steps yet.
  const lastMessage = messages[messages.length - 1];
  const isWaitingForStream =
    isStreaming &&
    lastMessage?.role === 'assistant' &&
    lastMessage.content === '' &&
    activeToolSteps.length === 0;

  // Render the messages area content — extracted to avoid nested ternaries
  // (no-nested-ternary rule). Three mutually exclusive states:
  // loading skeleton → suggested questions → message list.
  const renderMessagesContent = () => {
    if (showLoading) return <ChatSkeleton />;
    if (!hasMessages) {
      return (
        <SuggestedQuestions
          suggestions={suggestions}
          interviewGoal={interviewGoal}
          respondentCount={respondentCount}
          onSelectQuestion={handleSuggestionSelect}
          disabled={isCreatingThread || isStreaming}
        />
      );
    }
    return (
      <div className="px-5 py-5 max-w-4xl mx-auto">
        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            allArtifacts={allArtifacts}
            projectId={projectId}
            isStreaming={
              isStreaming && msg.role === 'assistant' && msg === messages[messages.length - 1]
            }
            onContinue={
              msg.truncation_reason && !isStreaming
                ? () => onSendMessage('Please continue where you left off.')
                : undefined
            }
          />
        ))}

        {/* "Connecting..." indicator before the SSE starts streaming */}
        {isWaitingForStream && (
          <div className="ml-9 mb-4 flex items-center gap-2 text-xs text-neutral-400">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>{lang.get('msg.thinking')}</span>
          </div>
        )}

        {/* Inline tool progress — only show RUNNING steps. */}
        {isStreaming && activeToolSteps.length > 0 && (
          <div className="ml-9 mb-4">
            <ToolProgressStepper steps={activeToolSteps} />
            {activeToolSteps
              .filter((s) => s.status === 'running' && s.substep)
              .map((s) => (
                <NonBlockingProgressStrip
                  key={`substep-${s.tool_call_id}`}
                  variant="inline"
                  title={s.substep!.name}
                  current={s.substep!.current}
                  total={s.substep!.total}
                  startedAt={s.substep!.started_at}
                />
              ))}
          </div>
        )}

        {/* D.7: stale comparison refresh button */}
        {activeToolSteps
          .filter((s) => s.status === 'stale' && s.next_action?.kind === 'regenerate_comparison')
          .map((s) => {
            const action = s.next_action!;
            if (action.kind !== 'regenerate_comparison') return null;
            const { comparison_id, filter_id_a, filter_id_b, label } = action;
            return (
              <div key={`regen-${s.tool_call_id}`} className="ml-9 mb-4">
                {isRegeneratingComparison ? (
                  <NonBlockingProgressStrip
                    variant="inline"
                    title={regenSubstep || lang.get('msg.refreshingComparison')}
                    startedAt={regenStartedAt}
                  />
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50"
                    onClick={() =>
                      onRegenerateComparison?.(comparison_id, filter_id_a, filter_id_b)
                    }
                    disabled={isRegeneratingComparison || isRerunLoading}
                    title={isRerunLoading ? lang.get('msg.waitForFullRerun') : label}
                  >
                    <RefreshCw className="h-3 w-3" />
                    {label}
                  </Button>
                )}
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Context bar. Fixed h-14 (56px) so the three Research Assistant
          columns (threads, chat, artifacts) have aligned header bars.
          Title, respondent count, and filter badge sit inline on a
          single row separated by a middle-dot so the whole bar fits in
          56px without wrapping. */}
      <div className="h-14 flex items-center gap-3 px-5 border-b border-neutral-200 bg-gradient-to-r from-white via-white to-primary/5/30 shrink-0">
        <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-neutral-800 truncate">
            {projectTitle || lang.get('msg.raResearchAssistant')}
          </h3>
          {respondentCount !== undefined && respondentCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] text-neutral-500 shrink-0">
              <span className="text-neutral-300">·</span>
              <Users className="h-3 w-3 text-neutral-400" />
              {respondentCount}{' '}
              {respondentCount === 1 ? lang.get('msg.raInterview') : lang.get('msg.raInterviews')}
            </span>
          )}
          {filterName && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-chat-primary bg-light-pink border border-chat-primary/30 px-2 py-0.5 rounded-full shrink-0">
              <Filter className="h-2.5 w-2.5" />
              {filterName}
            </span>
          )}
        </div>
        {headerRight && <div className="shrink-0">{headerRight}</div>}
      </div>

      {/* Error banner — Sprint A audit fix: OUTSIDE the scrollable container
          so it stays visible regardless of scroll position. D.2: replaced the
          single hard-coded template with ResearchAssistantErrorBanner which
          renders distinct copy + actions for all 9 SafeErrorCodes.
          D.8 triple-stack suppression: when D.8's sticky banner is already
          visible for ANALYSIS_OUTDATED, we collapse the full red banner to a
          quieter inline notice (rendered below in the input area). */}
      {error && !(suppressAnalysisOutdatedBanner && error.code === 'ANALYSIS_OUTDATED') && (
        <ResearchAssistantErrorBanner
          code={error.code}
          message={error.message}
          correlationId={error.correlationId}
          retriable={error.retriable}
          onRetry={onRetry}
          onDismiss={onDismissError}
          onOpenThreadList={onOpenThreadList}
          onReRunAnalysis={onReRunAnalysis}
          retryAfterSec={error.retryAfterSec}
        />
      )}

      {/* Messages area — role="log" with polite aria-live so screen
          readers announce newly arriving messages without interrupting
          whatever the user is currently reading. aria-relevant="additions"
          stops streaming-text deltas from being re-announced as
          remove+add pairs on every chunk. */}
      <div
        ref={messagesContainerRef}
        role="log"
        aria-label="Research Assistant chat messages"
        aria-live="polite"
        aria-relevant="additions"
        aria-atomic="false"
        className="flex-1 overflow-y-auto"
      >
        {renderMessagesContent()}

        <div ref={messagesEndRef} />
      </div>

      {/* Re-run banner — shown above input when analysis is running */}
      {isRerunLoading && (
        <div className="shrink-0 border-t border-amber-200 bg-amber-50 px-5 py-2.5">
          <div className="flex items-center gap-2 max-w-4xl mx-auto text-xs text-amber-700">
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
            {lang.get('msg.analysisInProgressRA')}
          </div>
        </div>
      )}

      {/* D.8 triple-stack suppression: quieter inline notice when the full
          ANALYSIS_OUTDATED banner is suppressed by D.8's sticky banner.
          Renders in the input area so it doesn't block the message thread. */}
      {suppressAnalysisOutdatedBanner && error?.code === 'ANALYSIS_OUTDATED' && (
        <div className="shrink-0 border-t border-neutral-200 bg-neutral-50 px-5 py-2">
          <p className="text-xs text-neutral-500 max-w-4xl mx-auto">
            {lang.get('msg.threadPausedDuringRerun')}
          </p>
        </div>
      )}

      {/* Input area — hero styling on the empty state so the first
          interaction feels inviting; compact once the conversation has
          started so it doesn't dominate the scrollable thread. */}
      <div
        className={cn(
          'shrink-0 border-t border-neutral-200 bg-gradient-to-b from-white to-neutral-50/40 px-5',
          isEmptyState ? 'py-5' : 'py-4',
        )}
      >
        <div className="flex items-end gap-2 max-w-4xl mx-auto">
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={(() => {
                if (isRerunLoading) return lang.get('msg.analysisInProgress');
                if (isCreatingThread) return lang.get('msg.raCreatingThread');
                if (isStreaming) return lang.get('msg.raComposeMessage');
                return lang.get('msg.raAskAboutResearch');
              })()}
              className={cn(
                'w-full resize-none rounded-xl border-2 text-neutral-800 placeholder:text-neutral-400 transition-all',
                isEmptyState ? 'px-5 py-4 text-base' : 'px-4 py-3 text-sm',
                isRerunLoading
                  ? 'border-amber-200 bg-amber-50/50 cursor-not-allowed'
                  : 'border-neutral-200 bg-white',
                'focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary',
              )}
              rows={1}
              disabled={isCreatingThread || isRerunLoading}
            />
          </div>
          {isStreaming ? (
            <Button
              variant="outline"
              size="icon"
              className={cn(
                'rounded-xl shrink-0 border-2 border-red-200 hover:border-red-300 hover:bg-red-50',
                isEmptyState ? 'h-14 w-14' : 'h-12 w-12',
              )}
              onClick={onStopStreaming}
              aria-label="Stop generation"
              title="Stop generation"
            >
              <Square
                className={cn('text-red-500 fill-red-500', isEmptyState ? 'h-5 w-5' : 'h-4 w-4')}
              />
            </Button>
          ) : (
            <Button
              size="icon"
              className={cn(
                'rounded-xl shrink-0 transition-all',
                isEmptyState ? 'h-14 w-14' : 'h-12 w-12',
                'bg-primary hover:bg-darker-primary',
                'shadow-sm hover:shadow-md',
                'disabled:from-neutral-300 disabled:to-neutral-400 disabled:shadow-none',
              )}
              onClick={handleSubmit}
              disabled={!inputValue.trim() || isCreatingThread || isRerunLoading}
              aria-label="Send message"
              title="Send message (Enter)"
            >
              {isCreatingThread ? (
                <Loader2
                  className={cn('animate-spin text-white', isEmptyState ? 'h-5 w-5' : 'h-4 w-4')}
                />
              ) : (
                <Send className={cn('text-white', isEmptyState ? 'h-5 w-5' : 'h-4 w-4')} />
              )}
            </Button>
          )}
        </div>
        <p
          className={cn(
            'text-neutral-400 text-center mt-2',
            isEmptyState ? 'text-[11px]' : 'text-[10px]',
          )}
        >
          {lang.get('msg.raEnterToSend')} · {lang.get('msg.raShiftEnterNewline')}
        </p>
      </div>
    </div>
  );
}

export default ChatThread;
